import { useState, useEffect, useRef, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import get from 'lodash/get';

// MUI
import {
  Box,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
} from '@material-ui/core';
import FullScreenIcon from '@material-ui/icons/Fullscreen';
import FullScreenExitIcon from '@material-ui/icons/FullscreenExit';
import WarningRoundedIcon from '@material-ui/icons/WarningRounded';
import ErrorRoundedIcon from '@material-ui/icons/ErrorRounded';

// app
import { useAccordionStyles, useSummaryTableStyles } from './SummaryTable.styles';

import { Accordion, Button, Empty, Tooltip } from 'components';
import { CircularProgress } from 'components/CircularProgress';
import { useDeleteWebSocketConnection } from 'lib/binderManagement';
import { useWebSockets } from 'hooks';
import { UploadBDXContext } from '../../UploadBDX.context';
import { ReactComponent as Loading } from 'assets/svg/loading.svg';
import { ReactComponent as BlockedRulesImage } from 'assets/svg/mapping-not-found.svg';
import { SUMMARY_ACCORDIONS } from 'consts';
import * as utils from 'utils';

const ROW_PER_PAGE = 100;

export const SummaryTable = ({ toggleFullScreen, fullView, fullScreen }) => {
  const classes = useSummaryTableStyles({
    fullView,
    fullScreen,
  });
  const { requestId } = useContext(UploadBDXContext);

  const [page, setPage] = useState(0);
  const [data, setData] = useState({
    headers: [],
    rows: [],
    exceptions: [],
    totalRows: 0,
    ruleName: '',
    message: '',
    contactAdmin: false,
    sessionId: '',
  });

  const client = useRef();
  const subscription = useRef();
  const { connectWebSocket } = useWebSockets();
  const { mutate: deleteWebSocketConnection } = useDeleteWebSocketConnection();

  const endpoint = useSelector((state) => get(state, 'config.vars.endpoint'));
  const { getAccessTokenSilently } = useAuth0();
  const accessToken = getAccessTokenSilently();
  const progress = data.rows?.length > 0 && data.totalRows > 0 ? (data.rows?.length / data.totalRows).toFixed(2) * 100 : 0;

  useEffect(() => {
    const { stompClient, sessionId } = connectWebSocket(`${endpoint.bm.fileProcessing}/ws/v1/bm?requestId=${requestId}`);
    client.current = stompClient;
    client.current.connect({ Authorization: `Bearer ${accessToken}` }, function (frame) {
      console.log('Connected: ' + frame);

      subscription.current = client.current.subscribe(`/topic/${sessionId}`, function (message) {
        let res = JSON.parse(message.body);
        if (progress === 100) {
          client.current.disconnect();
          client.current.unsubscribe(`/topic/${sessionId}`);
        }

        const newRows = res?.processedRows || [];
        setData((prevData) => ({
          headers: res?.headers,
          rows: [...prevData.rows, ...newRows],
          totalRows: res?.totalRows,
          exceptions: res?.exceptions,
          ruleName: res?.ruleName,
          message: res?.message,
          contactAdmin: res?.contactAdmin,
          sessionId,
        }));
      });
    });
    return () => {
      subscription.current?.unsubscribe(`/topic/${sessionId}`);
      client.current?.disconnect();
      deleteWebSocketConnection({ sessionId });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (progress === 100) {
      if (Boolean(data?.sessionId)) {
        subscription.current?.unsubscribe(`/topic/${data?.sessionId}`);
      }
      client.current?.disconnect();
    }
  }, [data?.sessionId, progress]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  if ((data?.ruleName && data.message) || data?.contactAdmin) {
    const title = data?.contactAdmin
      ? utils.string.t('notification.bdx.contactAdmin')
      : utils.string.t('notification.bdx.brokenRules', { ruleName: data.ruleName });

    return <Empty width={400} title={title} icon={<BlockedRulesImage />} padding />;
  }

  return (
    <>
      <Grid container className={classes.summaryHeader}>
        <Grid
          container
          spacing={2}
          style={{
            display: 'flex',
            alignItems: 'baseline',
            width: 'auto',
          }}
        >
          <Grid item>
            <Typography variant="h2" noWrap>
              {utils.string.t('form.bdx.bdxSample')}
            </Typography>
          </Grid>

          <Grid item>
            <ColorIndicator text={utils.string.t('form.bdx.edgeColumns')} color="#334762" />
          </Grid>
          <Grid item>
            <ColorIndicator text={utils.string.t('form.bdx.excelColumns')} color="#107C41" />
          </Grid>
        </Grid>
        <Grid item style={{ margin: '3px' }}>
          <Box display="flex" alignItems="center">
            <TablePagination
              component="div"
              page={page}
              count={data.rows?.length}
              rowsPerPage={100}
              rowsPerPageOptions={[]}
              onPageChange={handleChangePage}
              centered
            />
            <CircularProgress value={progress} />
            <Button
              icon={fullView ? FullScreenExitIcon : FullScreenIcon}
              variant="text"
              onClick={() => {
                toggleFullScreen();
              }}
              data-testid="bdxsample-fullscreen-button"
            />
          </Box>
        </Grid>
      </Grid>
      <div className={classes.root}>
        {data.rows?.length > 0 ? (
          <>
            <TableContainer className={classes.container}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow>
                    {data.headers?.length > 0 &&
                      data.headers.map((header) => (
                        <TableCell key={header.matchedColumnName} align="center" className={classes.matchedColumnName}>
                          <Tooltip title={<HeaderTooltip {...header} />} placement="top" rich>
                            {header.matchedColumnName}
                          </Tooltip>
                        </TableCell>
                      ))}
                  </TableRow>
                  <TableRow>
                    {data.headers?.length > 0 &&
                      data.headers.map((header) => (
                        <TableCell key={header.odsColumnName} align="center">
                          <Tooltip title={<HeaderTooltip {...header} />} placement="top" rich>
                            {header.odsColumnName}
                          </Tooltip>
                        </TableCell>
                      ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.rows?.length > 0 &&
                    data.headers?.length > 0 &&
                    data.rows?.slice(page * ROW_PER_PAGE, page * ROW_PER_PAGE + ROW_PER_PAGE)?.map((row, index) => {
                      return (
                        <TableRow hover role="checkbox" tabIndex={-1} key={row.code}>
                          {data.headers.map((header) => {
                            const value = row?.columns?.find((r) => r.odsColumnName === header.odsColumnName)?.value || '';
                            return (
                              <TableCell key={`${header.odsColumnName}-${index}`} align="center" className={classes.autoCompleteLabel}>
                                {value?.length > 15 ? (
                                  <Tooltip title={value} placement="top" rich>
                                    {value}
                                  </Tooltip>
                                ) : (
                                  value
                                )}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            {data?.exceptions?.length > 0 ? <ExceptionAccordion exceptions={data?.exceptions} /> : null}
          </>
        ) : (
          <Empty width={360} title={utils.string.t('form.bdx.summaryInProgress')} icon={<Loading />} padding />
        )}
      </div>
    </>
  );
};

const HeaderTooltip = ({ matchedColumnName, odsColumnName }) => {
  return (
    <Box>
      <Typography variant="body2" color="text.secondary">
        Excel: <span style={{ fontWeight: 600 }}>{matchedColumnName}</span>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Edge: <span style={{ fontWeight: 600 }}>{odsColumnName}</span>
      </Typography>
    </Box>
  );
};

const ColorIndicator = ({ text, color }) => (
  <Grid container direction="row" alignItems="center">
    <Grid item>
      <Box
        sx={{
          width: '0.75rem',
          height: '0.75rem',
          backgroundColor: color,
          borderRadius: '1rem',
          marginRight: '0.5rem',
        }}
      />
    </Grid>
    <Grid item>
      <Typography>{text}</Typography>
    </Grid>
  </Grid>
);

const ExceptionAccordion = ({ exceptions }) => {
  const classes = useAccordionStyles();
  const [expanded, setExpanded] = useState(false);

  const handleAccordion = (panel) => (_, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <div className={classes.accordion}>
      <Accordion
        icon={true}
        secondaryIcon={
          exceptions?.length > 0 && (
            <Chip size="small" style={{ backgroundColor: '#edac00', color: '#FFFFFF', marginLeft: '5px' }} label={exceptions?.length} />
          )
        }
        expanded={expanded === SUMMARY_ACCORDIONS.EXCEPTIONS}
        title={{ text: 'Items for review', type: 'h2' }}
        onChange={handleAccordion(SUMMARY_ACCORDIONS.EXCEPTIONS)}
      >
        <List>
          {exceptions?.map((err) => (
            <ListItem>
              <ListItemIcon>
                {err.level === 'Error' ? (
                  <ErrorRoundedIcon style={{ color: '#ef5350' }} />
                ) : (
                  <WarningRoundedIcon style={{ color: '#ff9800' }} />
                )}
              </ListItemIcon>
              <ListItemText primary={err.message} />
            </ListItem>
          ))}
        </List>
      </Accordion>
    </div>
  );
};
